import Vue from 'vue'
import {
  S,
  G,
  M,
  A,
  PdSettingState,
  PdSettingPropTypes,
  PdSettingProperties,
  FormattedPdSetting,
  QualitySettingSet,
  QuantitySettingNames,
  QuantitySettingItem
} from './types'
import { PdSettingCheckProperties } from '@/types/pd'
import { Getters, Mutations, Actions } from 'vuex'
import { capitalize } from '@/utils/string'
import { WasteFluidDict, ExitsiteDict, SwellingDict } from '@/constants/pd'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  pdSetting: null
})

/* -------------------------------------------------------------------------- */
const qualitySettingSet: QualitySettingSet = {
  isWasteFluid: Object.keys(WasteFluidDict).map(Number),
  isExitsite: Object.keys(ExitsiteDict).map(Number),
  isSwelling: Object.keys(SwellingDict).map(Number)
}

const quantitySettingProperties: Array<QuantitySettingNames> = [
  'limitDrainVolume',
  'limitInfusionVolume',
  'limitWaterRemoval',
  'limitWeight',
  'limitUrineVolume',
  'limitWaterDrinkingVolume'
]

const checkPropertyMap = {
  limitDrainVolume: 'isLimitchkDrainVolume',
  limitInfusionVolume: 'isLimitchkInfusionVolume',
  limitWaterRemoval: 'isLimitchkWaterRemoval',
  limitWeight: 'isLimitchkWeight',
  limitUrineVolume: 'isLimitchkUrineVolume',
  limitWaterDrinkingVolume: 'isLimitchkWaterDrinkingVolume'
}

const getProperty = (
  prefix: PdSettingPropTypes,
  name: string,
  suffix: string | number = ''
) =>
  `${prefix}${capitalize(name)}${String(suffix)}` as keyof PdSettingProperties

const extractQualitySettings = (
  pdSetting: PdSettingState,
  prefix: PdSettingPropTypes,
  name: string,
  values: number[]
) =>
  values
    .map((value: number) => ({
      setting: pdSetting[getProperty(prefix, name, value)],
      value
    }))
    .reduce(
      (collection, { setting, value }) =>
        setting !== 0 ? collection.concat(value) : collection,
      [] as number[]
    )

const extractQuantitySettings = (
  pdSetting: PdSettingState,
  prefix: PdSettingPropTypes,
  name: QuantitySettingNames
) => {
  const setting = {
    on:
      pdSetting[
        `${prefix}${capitalize(
          checkPropertyMap[name]
        )}` as keyof PdSettingCheckProperties
      ]
  } as { [key: string]: number | null }

  return ['low', 'high'].reduce(
    (settig, type: string) =>
      Object.assign(settig, {
        [type]: pdSetting[getProperty(prefix, name, capitalize(type))]
      }),
    setting
  ) as QuantitySettingItem
}

const getters: Getters<S, G> = {
  pdSetting: state => state.pdSetting,
  format: ({ pdSetting }) =>
    pdSetting === null
      ? null
      : ((['emg', 'warn'] as Array<PdSettingPropTypes>).reduce(
          (settings, type) =>
            Object.assign(settings, {
              [type]: Object.assign(
                (Object.keys(qualitySettingSet) as Array<
                  keyof QualitySettingSet
                >).reduce(
                  (qualities, key) =>
                    Object.assign(qualities, {
                      [key]: extractQualitySettings(
                        pdSetting,
                        type,
                        key,
                        qualitySettingSet[key]
                      )
                    }),
                  {}
                ),
                quantitySettingProperties.reduce(
                  (quantities, key) =>
                    Object.assign(quantities, {
                      [key]: extractQuantitySettings(pdSetting, type, key)
                    }),
                  {}
                )
              )
            }),
          {} as { [T in keyof FormattedPdSetting]?: FormattedPdSetting[T] }
        ) as FormattedPdSetting)
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  setPdSetting: (state, { pdSetting }) => {
    state.pdSetting = pdSetting
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async fetchOne({ commit }, { patientId }) {
    const pdSetting = await Vue.$api.pdSetting.fetchOne({ patientId })

    commit('setPdSetting', { pdSetting })
  },

  async update({ commit }, { patientId, payload }) {
    const pdSetting = await Vue.$api.pdSetting.update({ patientId, payload })
    commit('setPdSetting', { pdSetting })
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
